// Which interface the user sees: "classic" (the default) or "new".
//
// The choice is kept in localStorage so it survives reloads and logins on this device,
// and it is applied as the ln-ui class on <body>. Every rule in newUi.css is scoped under
// body.ln-ui, so a user who never switches sees the classic UI exactly as before.

export const UI_CLASSIC = "classic";
export const UI_NEW = "new";

const STORAGE_KEY = "leafnet.uiMode";
const INTRO_KEY = "leafnet.uiMode.introSeen";
const BODY_CLASS = "ln-ui";

const listeners = new Set();

// Storage can throw (private windows, blocked site data); the UI then just stays classic.
function read(key) {
  try {
    return window.localStorage.getItem(key);
  } catch (e) {
    return null;
  }
}

function write(key, value) {
  try {
    window.localStorage.setItem(key, value);
  } catch (e) {
    // Ignored: the switch still works for this page view.
  }
}

export function getUiMode() {
  return read(STORAGE_KEY) === UI_NEW ? UI_NEW : UI_CLASSIC;
}

export function applyUiMode(mode = getUiMode()) {
  document.body.classList.toggle(BODY_CLASS, mode === UI_NEW);
}

export function setUiMode(mode) {
  const next = mode === UI_NEW ? UI_NEW : UI_CLASSIC;
  write(STORAGE_KEY, next);
  applyUiMode(next);
  listeners.forEach((fn) => fn(next));
}

// Returns an unsubscribe function, so it can be returned straight from a useEffect.
export function subscribeUiMode(fn) {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

export function hasSeenUiIntro() {
  return read(INTRO_KEY) === "1";
}

export function markUiIntroSeen() {
  write(INTRO_KEY, "1");
}

// A switch made in another tab reaches this one through the storage event.
if (typeof window !== "undefined") {
  window.addEventListener("storage", (e) => {
    if (e.key !== STORAGE_KEY) return;
    const mode = getUiMode();
    applyUiMode(mode);
    listeners.forEach((fn) => fn(mode));
  });
}
